import {
  ProductStatusSchema
} from "../chunk-DTQF5GFJ.js";
import {
  PortalUserRoleSchema
} from "../chunk-V3C3SGOL.js";
import {
  MetrcAlreadyImportedRowSchema,
  MetrcCandidatePackageSchema,
  MetrcDriftClassSchema,
  MetrcDriftDetectedRowSchema,
  MetrcFindCandidatesRequestSchema,
  MetrcFindCandidatesResponseSchema,
  MetrcImportPackagesRequestSchema,
  MetrcImportResultPackageSchema,
  MetrcImportResultRejectionSchema,
  MetrcImportResultResponseSchema,
  MetrcMatchStrategySchema,
  MetrcVariantCaseConfigSchema
} from "../chunk-WCS62LTD.js";

// inventory/inventory.schema.ts
import { z } from "zod";
var StockLocationDetailDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  isActive: z.boolean(),
  // Address details (optional, may be null)
  address: z.object({
    address1: z.string(),
    address2: z.string().nullable(),
    city: z.string().nullable(),
    province: z.string().nullable(),
    postalCode: z.string().nullable(),
    countryCode: z.string()
  }).nullable(),
  // Compliance license number (from linked compliance.licenses record)
  licenseNumber: z.string().nullable().optional(),
  // Owning organization (for cross-org visual distinction in dropdowns)
  organizationId: z.string().uuid().optional(),
  organizationName: z.string().optional(),
  // Supplier's access permissions
  canView: z.boolean(),
  canEdit: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var MetrcImportContextSourceSchema = z.enum([
  "metrc_origin",
  "primary",
  "assigned_location",
  "manual_required"
]);
var OrgBrandMetrcImportContextDtoSchema = z.object({
  brandId: z.string().uuid(),
  stockLocationId: z.string().uuid().nullable(),
  stockLocationName: z.string().nullable(),
  licenseNumber: z.string().nullable(),
  resolutionSource: MetrcImportContextSourceSchema
});
var InventoryItemListingDtoSchema = z.object({
  // Inventory item details
  id: z.string().uuid(),
  // inventory_item.id
  sku: z.string().nullable(),
  title: z.string().nullable(),
  // From inventory_item.title or product.title
  // Product info (joined from product tables)
  productId: z.string().uuid().nullable(),
  productName: z.string().nullable(),
  productStatus: ProductStatusSchema.nullable(),
  // Product status (for deactivation display)
  isSample: z.boolean(),
  // Brand info (joined from product → brand)
  brandId: z.string().uuid().nullable(),
  brandName: z.string().nullable(),
  // Inventory level at this location
  inventoryLevelId: z.string().uuid(),
  stockedQuantity: z.number().int(),
  reservedQuantity: z.number().int(),
  incomingQuantity: z.number().int(),
  availableQuantity: z.number().int(),
  // Timestamps
  updatedAt: z.string().datetime()
});
var InventoryListingResponseSchema = z.object({
  items: z.array(InventoryItemListingDtoSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
  hasMore: z.boolean()
});
var InventoryListingQuerySchema = z.object({
  // Filter by member/organization (for access control)
  memberId: z.string().uuid().optional(),
  organizationId: z.string().uuid().optional(),
  // Optional filters
  brandId: z.string().uuid().optional(),
  search: z.string().optional(),
  // Search by SKU or title
  // Pagination
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
  // Admin bypass (internal use only - bypasses access and brand ownership checks)
  isAdmin: z.boolean().optional().default(false)
});
var UpdateInventoryLevelDtoSchema = z.object({
  stockedQuantity: z.number().int().min(0)
});
var AddProductToInventoryInputSchema = z.object({
  productId: z.string().uuid(),
  initialQuantity: z.number().int().min(0).default(0),
  reason: z.string().optional()
});
var InventoryLevelDtoSchema = z.object({
  id: z.string().uuid(),
  inventoryItemId: z.string().uuid(),
  stockLocationId: z.string().uuid(),
  stockedQuantity: z.number().int(),
  incomingQuantity: z.number().int(),
  metadata: z.record(z.unknown()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var InventoryAdjustmentTypeSchema = z.enum(["set", "increment", "decrement", "sync"]);
var CreateInventoryAdjustmentInputSchema = z.object({
  inventoryLevelId: z.string().uuid(),
  portalUserId: z.string().uuid().nullable(),
  adjustmentType: InventoryAdjustmentTypeSchema,
  quantityBefore: z.number().int(),
  quantityAfter: z.number().int(),
  reason: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).optional()
});
var InventoryAdjustmentSchema = z.object({
  id: z.string().uuid(),
  inventoryLevelId: z.string().uuid(),
  portalUserId: z.string().uuid().nullable(),
  adjustmentType: InventoryAdjustmentTypeSchema,
  quantityBefore: z.number().int(),
  quantityAfter: z.number().int(),
  reason: z.string().nullable(),
  metadata: z.record(z.unknown()).optional(),
  createdAt: z.date()
});
var InventoryTransferStatusSchema = z.enum(["pending", "in_transit", "completed", "cancelled"]);
var InventoryTransferDtoSchema = z.object({
  id: z.string().uuid(),
  inventoryItemId: z.string().uuid(),
  fromStockLocationId: z.string().uuid(),
  toStockLocationId: z.string().uuid(),
  quantity: z.number().int().positive(),
  status: InventoryTransferStatusSchema,
  initiatedByPortalUserId: z.string().uuid().nullable(),
  receivedByPortalUserId: z.string().uuid().nullable(),
  notes: z.string().nullable(),
  referenceNumber: z.string().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  shippedAt: z.string().datetime().nullable(),
  receivedAt: z.string().datetime().nullable(),
  cancelledAt: z.string().datetime().nullable()
});
var InventoryTransferListingDtoSchema = InventoryTransferDtoSchema.extend({
  // Inventory item info
  sku: z.string().nullable(),
  productId: z.string().uuid().nullable(),
  productName: z.string().nullable(),
  brandName: z.string().nullable(),
  // Location names
  fromLocationName: z.string(),
  toLocationName: z.string(),
  // User names
  initiatedByName: z.string().nullable(),
  receivedByName: z.string().nullable(),
  // Supplier info (for admin global view)
  supplierId: z.string().uuid(),
  supplierName: z.string()
});
var CreateInventoryTransferInputSchema = z.object({
  inventoryItemId: z.string().uuid(),
  fromStockLocationId: z.string().uuid(),
  toStockLocationId: z.string().uuid(),
  quantity: z.number().int().positive(),
  notes: z.string().optional(),
  referenceNumber: z.string().optional(),
  // Instant transfer skips pending/in_transit and directly completes
  instant: z.boolean().default(false)
});
var UpdateTransferStatusInputSchema = z.object({
  action: z.enum(["ship", "receive", "cancel"]),
  notes: z.string().optional()
});
var TransferListingQuerySchema = z.object({
  memberId: z.string().uuid().optional(),
  organizationId: z.string().uuid().optional(),
  stockLocationId: z.string().uuid().optional(),
  productId: z.string().uuid().optional(),
  status: InventoryTransferStatusSchema.optional(),
  direction: z.enum(["incoming", "outgoing", "all"]).default("all"),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25)
});
var TransferListingResponseSchema = z.object({
  items: z.array(InventoryTransferListingDtoSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
  hasMore: z.boolean()
});
var AdminStockLocationDtoSchema = StockLocationDetailDtoSchema.extend({
  supplierId: z.string().uuid().nullable(),
  supplierName: z.string().nullable()
});
var AdminStockLocationFiltersSchema = z.object({
  isActive: z.boolean().optional(),
  supplierId: z.string().uuid().optional(),
  limit: z.number().int().optional(),
  offset: z.number().int().optional()
});
var AdminStockLocationResponseSchema = z.object({
  items: z.array(AdminStockLocationDtoSchema),
  total: z.number().int()
});
var CreateStockLocationAddressInputSchema = z.object({
  address1: z.string().min(1, "Address line 1 is required"),
  address2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  province: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
  countryCode: z.string().default("US"),
  phone: z.string().nullable().optional()
});
var CreateStockLocationInputSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  supplierId: z.string().uuid("Valid supplier ID required"),
  // Exactly one required: select existing address OR create new one
  addressId: z.string().uuid().nullable().optional(),
  newAddress: CreateStockLocationAddressInputSchema.nullable().optional(),
  isActive: z.boolean().default(true),
  /** Metrc license number to link to this location. Validated against Metrc API on create. */
  metrcLicenseNumber: z.string().min(1).optional()
}).refine(
  (data) => !!(data.addressId || data.newAddress),
  { message: "A physical address is required \u2014 provide either addressId or newAddress" }
).refine(
  (data) => !(data.addressId && data.newAddress),
  { message: "Provide either addressId or newAddress, not both" }
);
var StockLocationAddressListItemSchema = z.object({
  id: z.string(),
  address1: z.string(),
  address2: z.string().nullable(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  postalCode: z.string().nullable(),
  countryCode: z.string(),
  displayText: z.string()
});
var SupplierBrandListItemSchema = z.object({
  id: z.string(),
  name: z.string()
});
var StockLocationBrandAssignmentDtoSchema = z.object({
  brandId: z.string().uuid(),
  isPrimary: z.boolean(),
  isMetrcOrigin: z.boolean(),
  isActive: z.boolean()
});
var UpsertStockLocationBrandAssignmentInputSchema = z.object({
  brandId: z.string().uuid(),
  isPrimary: z.boolean().optional(),
  isMetrcOrigin: z.boolean().optional()
});
var UpdateStockLocationBrandAssignmentsDtoSchema = z.object({
  assignments: z.array(UpsertStockLocationBrandAssignmentInputSchema)
});
var UpdateStockLocationBrandsDtoSchema = z.object({
  brandIds: z.array(z.string().uuid())
});
var CreateOrgStockLocationInputSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  isActive: z.boolean().default(true),
  // Address: select existing OR create new
  addressId: z.string().uuid().nullable().optional(),
  newAddress: CreateStockLocationAddressInputSchema.nullable().optional(),
  /** Metrc license number to look up and link compliance_license_id */
  metrcLicenseNumber: z.string().min(1).optional(),
  /** Optional metadata */
  metadata: z.record(z.unknown()).optional()
}).refine(
  (data) => !!(data.addressId || data.newAddress),
  { message: "A physical address is required \u2014 provide either addressId or newAddress" }
).refine(
  (data) => !(data.addressId && data.newAddress),
  { message: "Provide either addressId or newAddress, not both" }
);

// inventory/inventory-access.schema.ts
import { z as z2 } from "zod";
var LocationAccessUserDtoSchema = z2.object({
  id: z2.string().uuid(),
  name: z2.string(),
  email: z2.string().email(),
  role: PortalUserRoleSchema,
  supplierId: z2.string().uuid().nullable(),
  supplierName: z2.string().nullable(),
  canView: z2.boolean(),
  canEdit: z2.boolean()
});
var StockLocationAccessResponseSchema = z2.object({
  locationId: z2.string().uuid(),
  locationName: z2.string(),
  users: z2.array(LocationAccessUserDtoSchema),
  totalUsers: z2.number().int()
});
var ActivityUserSchema = z2.object({
  id: z2.string().uuid(),
  name: z2.string(),
  role: PortalUserRoleSchema
});
var EnrichedInventoryAdjustmentDtoSchema = z2.object({
  id: z2.string().uuid(),
  inventoryLevelId: z2.string().uuid(),
  adjustmentType: InventoryAdjustmentTypeSchema,
  quantityBefore: z2.number().int(),
  quantityAfter: z2.number().int(),
  reason: z2.string().nullable(),
  createdAt: z2.string().datetime(),
  user: ActivityUserSchema.nullable(),
  productName: z2.string().nullable(),
  sku: z2.string().nullable()
});
var StockLocationActivityQuerySchema = z2.object({
  limit: z2.coerce.number().int().min(1).max(100).default(25),
  offset: z2.coerce.number().int().min(0).default(0)
});
var StockLocationActivityResponseSchema = z2.object({
  locationId: z2.string().uuid(),
  locationName: z2.string(),
  activities: z2.array(EnrichedInventoryAdjustmentDtoSchema),
  total: z2.number().int(),
  hasMore: z2.boolean()
});

// inventory/inventory-batch.schema.ts
import { z as z3 } from "zod";
var InventoryBatchStatusSchema = z3.enum(["active", "missing", "fulfilled", "transferred"]);
var InventoryBatchSchema = z3.object({
  id: z3.string().uuid(),
  inventoryItemId: z3.string().uuid(),
  stockLocationId: z3.string().uuid(),
  // Batch identification
  batchNumber: z3.string().nullable(),
  lotNumber: z3.string().nullable(),
  // Quantities
  quantity: z3.number().int(),
  reservedQuantity: z3.number().int(),
  availableQuantity: z3.number().int(),
  // Generated column (quantity - reservedQuantity)
  // Dates for FIFO/FEFO
  receivedDate: z3.date(),
  manufactureDate: z3.date().nullable(),
  expiryDate: z3.date().nullable(),
  // Legacy physical columns. Non-Metrc compatibility create/update DTOs keep these null.
  metrcPackageTag: z3.string().nullable(),
  /** Immutable Metrc item id the package was imported under — used to detect item_id_changed drift independent of the variant pin. */
  metrcItemId: z3.number().int().nullable(),
  // Metrc import snapshot
  labTestStatus: z3.string().nullable(),
  packagedDate: z3.date().nullable(),
  // Lifecycle status
  status: InventoryBatchStatusSchema,
  fulfilledDestinationLicenseNumber: z3.string().nullable(),
  statusChangedAt: z3.date().nullable(),
  statusNote: z3.string().nullable(),
  // Standard fields
  metadata: z3.record(z3.unknown()),
  createdAt: z3.date(),
  updatedAt: z3.date()
});
var InventoryBatchDtoSchema = z3.object({
  id: z3.string().uuid(),
  inventoryItemId: z3.string().uuid(),
  stockLocationId: z3.string().uuid(),
  // Batch identification
  batchNumber: z3.string().nullable(),
  lotNumber: z3.string().nullable(),
  // Quantities
  quantity: z3.number().int(),
  reservedQuantity: z3.number().int(),
  availableQuantity: z3.number().int(),
  // Dates for FIFO/FEFO (ISO strings)
  receivedDate: z3.string(),
  // YYYY-MM-DD
  manufactureDate: z3.string().nullable(),
  expiryDate: z3.string().nullable(),
  // Legacy physical columns. Non-Metrc compatibility create/update DTOs keep these null.
  metrcPackageTag: z3.string().nullable(),
  metrcItemId: z3.number().int().nullable(),
  // Metrc import snapshot
  labTestStatus: z3.string().nullable(),
  packagedDate: z3.string().nullable(),
  // YYYY-MM-DD
  // Lifecycle status
  status: InventoryBatchStatusSchema,
  fulfilledDestinationLicenseNumber: z3.string().nullable(),
  statusChangedAt: z3.string().datetime().nullable(),
  statusNote: z3.string().nullable(),
  // Standard fields
  metadata: z3.record(z3.unknown()),
  createdAt: z3.string().datetime(),
  updatedAt: z3.string().datetime()
});
var InventoryBatchListingDtoSchema = InventoryBatchDtoSchema.extend({
  // Inventory item info
  sku: z3.string().nullable(),
  // Product info (joined from product tables)
  productId: z3.string().uuid().nullable(),
  productName: z3.string().nullable(),
  brandName: z3.string().nullable(),
  // Location info
  stockLocationName: z3.string()
});
var CreateInventoryBatchInputSchema = z3.object({
  inventoryItemId: z3.string().uuid(),
  stockLocationId: z3.string().uuid(),
  // Batch identification (at least one recommended but not required for legacy)
  batchNumber: z3.string().max(100).nullable().optional(),
  lotNumber: z3.string().max(100).nullable().optional(),
  // Quantities
  quantity: z3.number().int().min(0).default(0),
  reservedQuantity: z3.number().int().min(0).default(0),
  // Dates
  receivedDate: z3.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD format"),
  manufactureDate: z3.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD format").nullable().optional(),
  expiryDate: z3.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD format").nullable().optional(),
  // Legacy physical Metrc columns are null-only for non-Metrc batch/lot compatibility.
  metrcPackageTag: z3.null().optional(),
  metrcItemId: z3.null().optional(),
  // Metrc import snapshot
  labTestStatus: z3.string().max(50).nullable().optional(),
  packagedDate: z3.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD format").nullable().optional(),
  metadata: z3.record(z3.unknown()).optional()
});
var UpdateInventoryBatchInputSchema = z3.object({
  batchNumber: z3.string().max(100).nullable().optional(),
  lotNumber: z3.string().max(100).nullable().optional(),
  quantity: z3.number().int().min(0).optional(),
  reservedQuantity: z3.number().int().min(0).optional(),
  receivedDate: z3.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD format").optional(),
  manufactureDate: z3.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD format").nullable().optional(),
  expiryDate: z3.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD format").nullable().optional(),
  metrcPackageTag: z3.null().optional(),
  metrcItemId: z3.null().optional(),
  metadata: z3.record(z3.unknown()).optional()
});
var BatchListingQuerySchema = z3.object({
  // Required context
  supplierId: z3.string().uuid(),
  stockLocationId: z3.string().uuid(),
  // Optional filters
  search: z3.string().optional(),
  inventoryItemId: z3.string().uuid().optional(),
  batchNumber: z3.string().optional(),
  lotNumber: z3.string().optional(),
  // Only show batches with available quantity > 0
  availableOnly: z3.coerce.boolean().default(false),
  // Pagination
  page: z3.coerce.number().int().min(1).default(1),
  pageSize: z3.coerce.number().int().min(1).max(100).default(50),
  // Sorting
  sortBy: z3.enum(["received_date", "expiry_date", "quantity", "created_at"]).default("received_date"),
  sortOrder: z3.enum(["asc", "desc"]).default("asc")
});
var BatchListingResponseSchema = z3.object({
  items: z3.array(InventoryBatchListingDtoSchema),
  total: z3.number().int(),
  page: z3.number().int(),
  pageSize: z3.number().int(),
  hasMore: z3.boolean()
});
var BatchReservationSchema = z3.object({
  batchId: z3.string().uuid(),
  batchNumber: z3.string().nullable(),
  lotNumber: z3.string().nullable(),
  metrcPackageTag: z3.string().nullable().optional(),
  reservedQuantity: z3.number().int().positive(),
  // How much reserved from this batch
  receivedDate: z3.date(),
  expiryDate: z3.date().nullable()
});
var FifoReservationResultSchema = z3.object({
  // Successfully reserved batches
  reservations: z3.array(BatchReservationSchema),
  // Total quantity successfully reserved
  totalReserved: z3.number().int(),
  // Whether the full requested quantity was satisfied
  fullyReserved: z3.boolean(),
  // Shortfall if not fully reserved
  shortfall: z3.number().int()
});
var ReceiveBatchInputSchema = z3.object({
  // Product/item identification
  inventoryItemId: z3.string().uuid(),
  stockLocationId: z3.string().uuid(),
  // Batch info
  batchNumber: z3.string().max(100).nullable().optional(),
  lotNumber: z3.string().max(100).nullable().optional(),
  quantity: z3.number().int().positive("Quantity must be positive"),
  // Dates
  receivedDate: z3.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD format").optional(),
  // Defaults to today
  manufactureDate: z3.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD format").nullable().optional(),
  expiryDate: z3.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD format").nullable().optional(),
  // Compliance
  metrcPackageTag: z3.null().optional(),
  // Optional notes for adjustment record
  reason: z3.string().optional()
});
var MetrcLabStatusSchema = z3.enum(["TestPassed", "RetestPassed"]);
var MetrcImportUpdateModeSchema = z3.enum(["set", "increment"]);
var MetrcCsvRowSchema = z3.object({
  tag: z3.string(),
  item: z3.string(),
  quantity: z3.number(),
  unitOfMeasure: z3.string(),
  labTestStatus: z3.string(),
  packagedDate: z3.string(),
  receivedDate: z3.string().nullable().optional(),
  productionBatchNumber: z3.string().nullable(),
  /** Metrc location/room within the facility */
  location: z3.string().optional()
});
var MetrcCsvImportInputSchema = z3.object({
  stockLocationId: z3.string().uuid(),
  updateMode: MetrcImportUpdateModeSchema,
  /** Selected item names from the CSV that map to this product */
  selectedItems: z3.array(z3.string()).min(1, "At least one item must be selected"),
  /** Parsed CSV rows (server re-validates) */
  rows: z3.array(MetrcCsvRowSchema).min(1, "At least one row required"),
  /** Optional Metrc config to update on the variant */
  metrcConfig: z3.object({
    unitWeight: z3.number().positive().optional(),
    unitsPerCase: z3.number().int().positive().optional()
  }).optional(),
  /** Original filename for audit trail */
  filename: z3.string().optional(),
  /** When true, batches that exist at other locations are moved to the target location */
  relocateDuplicates: z3.boolean().optional()
});
var MetrcImportCreatedPackageSchema = z3.object({
  tag: z3.string(),
  productionBatchNumber: z3.string().nullable(),
  quantity: z3.number(),
  packagedDate: z3.string()
});
var MetrcImportSkippedRowSchema = z3.object({
  tag: z3.string(),
  reason: z3.string()
});
var MetrcCrossLocationDuplicatesSummarySchema = z3.object({
  count: z3.number().int(),
  totalCases: z3.number().int(),
  locations: z3.array(
    z3.object({
      locationId: z3.string().uuid(),
      locationName: z3.string(),
      count: z3.number().int()
    })
  )
});
var MetrcImportMissingPackageSchema = z3.object({
  tag: z3.string(),
  previousQuantity: z3.number().int(),
  hasActiveReservation: z3.boolean()
});
var MetrcImportRecoveredPackageSchema = z3.object({
  tag: z3.string(),
  quantity: z3.number().int()
});
var MetrcCsvImportResponseSchema = z3.object({
  success: z3.boolean(),
  summary: z3.object({
    totalRowsProcessed: z3.number().int(),
    accepted: z3.number().int(),
    rejected: z3.number().int(),
    duplicates: z3.number().int(),
    loosePackages: z3.number().int(),
    totalCases: z3.number().int(),
    previousQuantity: z3.number().int(),
    newQuantity: z3.number().int(),
    updateMode: MetrcImportUpdateModeSchema,
    relocated: z3.number().int().optional(),
    missing: z3.number().int().optional(),
    recovered: z3.number().int().optional()
  }),
  created: z3.array(MetrcImportCreatedPackageSchema),
  rejected: z3.array(MetrcImportSkippedRowSchema),
  duplicates: z3.array(MetrcImportSkippedRowSchema),
  loose: z3.array(MetrcImportSkippedRowSchema),
  /** Present when cross-location duplicates were detected but not relocated */
  crossLocationDuplicates: MetrcCrossLocationDuplicatesSummarySchema.optional(),
  /** Packages that were active but not found in this CSV import — flagged for investigation */
  missingPackages: z3.array(MetrcImportMissingPackageSchema).optional(),
  /** Packages that were previously missing but reappeared in this CSV import */
  recoveredPackages: z3.array(MetrcImportRecoveredPackageSchema).optional()
});
var MetrcPackageFetchItemGroupSchema = z3.object({
  item: z3.string(),
  rowCount: z3.number().int(),
  totalQuantity: z3.number(),
  unitOfMeasure: z3.string()
});
var MetrcPackageFetchResponseSchema = z3.object({
  rows: z3.array(MetrcCsvRowSchema),
  itemGroups: z3.array(MetrcPackageFetchItemGroupSchema),
  licenseNumber: z3.string(),
  totalPackagesFetched: z3.number().int(),
  matchedPackages: z3.number().int()
});
var MetrcPackageReservationLinkSchema = z3.object({
  partitionId: z3.string().uuid(),
  displayRef: z3.string(),
  reservedQuantity: z3.number().int().positive(),
  orderedAt: z3.string().datetime()
});
var MetrcPackageFulfillmentLinkSchema = z3.object({
  partitionId: z3.string().uuid(),
  displayRef: z3.string(),
  fulfilledAt: z3.string().datetime(),
  destinationLicenseNumber: z3.string().nullable()
});
var MetrcPackageFacilitySchema = z3.object({
  stockLocationId: z3.string().uuid(),
  stockLocationName: z3.string(),
  facilityLicenseNumber: z3.string().nullable()
});
var MetrcPackageListingDtoSchema = z3.object({
  packageId: z3.string().uuid(),
  packageLabel: z3.string(),
  metrcItemId: z3.number().int().nullable(),
  sku: z3.string().nullable(),
  variantId: z3.string().uuid().nullable(),
  productionBatchNumber: z3.string().nullable(),
  labTestStatus: z3.string().nullable(),
  facility: MetrcPackageFacilitySchema,
  packagedDate: z3.string().nullable(),
  localState: InventoryBatchStatusSchema,
  statusChangedAt: z3.string().datetime().nullable(),
  reservations: z3.array(MetrcPackageReservationLinkSchema),
  fulfilledBy: MetrcPackageFulfillmentLinkSchema.nullable(),
  transferredTo: z3.object({
    stockLocationId: z3.string().uuid(),
    stockLocationName: z3.string()
  }).nullable()
});
var MetrcPackageListingQuerySchema = z3.object({
  status: z3.enum(["active", "inactive"]).default("active"),
  facility: z3.string().optional(),
  /**
   * Comma-separated production batch numbers. Empty or missing = no filter.
   * Comma separation keeps the ts-rest query string simple and deterministic.
   */
  productionBatchNumbers: z3.string().optional(),
  search: z3.string().optional(),
  page: z3.coerce.number().int().min(1).default(1),
  pageSize: z3.coerce.number().int().min(1).max(100).default(50)
});
var MetrcPackageProductionBatchSummarySchema = z3.object({
  productionBatchNumber: z3.string(),
  packageCount: z3.number().int()
});
var MetrcPackageListingResponseSchema = z3.object({
  items: z3.array(MetrcPackageListingDtoSchema),
  total: z3.number().int(),
  page: z3.number().int(),
  pageSize: z3.number().int(),
  hasMore: z3.boolean(),
  activeCount: z3.number().int(),
  inactiveCount: z3.number().int(),
  facilitiesInUse: z3.array(MetrcPackageFacilitySchema),
  /** Distinct production batch numbers for this product + tab, with their package counts. */
  productionBatchesInUse: z3.array(MetrcPackageProductionBatchSummarySchema)
});
var MetrcPackageRemoveRequestSchema = z3.object({
  packageIds: z3.array(z3.string().uuid()).min(1).max(100),
  reason: z3.string().max(500).optional()
});
var MetrcPackageRemoveResponseSchema = z3.object({
  removed: z3.array(z3.string().uuid())
});

// inventory/inventory-package.schema.ts
import { z as z4 } from "zod";
var IsoDateSchema = z4.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD format");
var InventoryPackageLocalStateSchema = InventoryBatchStatusSchema;
var InventoryPackageSchema = z4.object({
  id: z4.string().uuid(),
  inventoryItemId: z4.string().uuid(),
  stockLocationId: z4.string().uuid(),
  packageLabel: z4.string().nullable(),
  metrcItemId: z4.number().int().nullable(),
  isMetrcTracked: z4.boolean(),
  facilityLicenseNumber: z4.string().nullable(),
  quantity: z4.number(),
  receivedDate: z4.date(),
  productionBatchNumber: z4.string().nullable(),
  lotNumber: z4.string().nullable(),
  manufactureDate: z4.date().nullable(),
  expiryDate: z4.date().nullable(),
  labTestStatus: z4.string().nullable(),
  packagedDate: z4.date().nullable(),
  localState: InventoryPackageLocalStateSchema,
  fulfilledDestinationLicenseNumber: z4.string().nullable(),
  statusChangedAt: z4.date().nullable(),
  statusNote: z4.string().nullable(),
  createdAt: z4.date(),
  updatedAt: z4.date()
});
var InventoryPackageDtoSchema = z4.object({
  id: z4.string().uuid(),
  inventoryItemId: z4.string().uuid(),
  stockLocationId: z4.string().uuid(),
  packageLabel: z4.string().nullable(),
  metrcItemId: z4.number().int().nullable(),
  isMetrcTracked: z4.boolean(),
  facilityLicenseNumber: z4.string().nullable(),
  quantity: z4.number(),
  receivedDate: IsoDateSchema,
  productionBatchNumber: z4.string().nullable(),
  lotNumber: z4.string().nullable(),
  manufactureDate: IsoDateSchema.nullable(),
  expiryDate: IsoDateSchema.nullable(),
  labTestStatus: z4.string().nullable(),
  packagedDate: IsoDateSchema.nullable(),
  localState: InventoryPackageLocalStateSchema,
  fulfilledDestinationLicenseNumber: z4.string().nullable(),
  statusChangedAt: z4.string().datetime().nullable(),
  statusNote: z4.string().nullable(),
  createdAt: z4.string().datetime(),
  updatedAt: z4.string().datetime()
});
var InventoryPackageListingDtoSchema = InventoryPackageDtoSchema.extend({
  sku: z4.string().nullable(),
  productId: z4.string().uuid().nullable(),
  productName: z4.string().nullable(),
  brandName: z4.string().nullable(),
  stockLocationName: z4.string()
});
var InventoryPackageListingQuerySchema = z4.object({
  supplierId: z4.string().uuid(),
  stockLocationId: z4.string().uuid(),
  search: z4.string().optional(),
  inventoryItemId: z4.string().uuid().optional(),
  packageLabel: z4.string().optional(),
  productionBatchNumber: z4.string().optional(),
  lotNumber: z4.string().optional(),
  isMetrcTracked: z4.coerce.boolean().optional(),
  localState: InventoryPackageLocalStateSchema.optional(),
  page: z4.coerce.number().int().min(1).default(1),
  pageSize: z4.coerce.number().int().min(1).max(100).default(50),
  sortBy: z4.enum(["received_date", "expiry_date", "quantity", "created_at"]).default("received_date"),
  sortOrder: z4.enum(["asc", "desc"]).default("asc")
});
var InventoryPackageListingResponseSchema = z4.object({
  items: z4.array(InventoryPackageListingDtoSchema),
  total: z4.number().int(),
  page: z4.number().int(),
  pageSize: z4.number().int(),
  hasMore: z4.boolean()
});
var UpdateInventoryPackageInputSchema = z4.object({
  packageLabel: z4.string().max(100).nullable().optional(),
  metrcItemId: z4.number().int().nullable().optional(),
  facilityLicenseNumber: z4.string().max(64).nullable().optional(),
  receivedDate: IsoDateSchema.optional(),
  productionBatchNumber: z4.string().max(100).nullable().optional(),
  lotNumber: z4.string().max(100).nullable().optional(),
  manufactureDate: IsoDateSchema.nullable().optional(),
  expiryDate: IsoDateSchema.nullable().optional(),
  labTestStatus: z4.string().max(50).nullable().optional(),
  packagedDate: IsoDateSchema.nullable().optional()
});
export {
  ActivityUserSchema,
  AddProductToInventoryInputSchema,
  AdminStockLocationDtoSchema,
  AdminStockLocationFiltersSchema,
  AdminStockLocationResponseSchema,
  BatchListingQuerySchema,
  BatchListingResponseSchema,
  BatchReservationSchema,
  CreateInventoryAdjustmentInputSchema,
  CreateInventoryBatchInputSchema,
  CreateInventoryTransferInputSchema,
  CreateOrgStockLocationInputSchema,
  CreateStockLocationAddressInputSchema,
  CreateStockLocationInputSchema,
  EnrichedInventoryAdjustmentDtoSchema,
  FifoReservationResultSchema,
  InventoryAdjustmentSchema,
  InventoryAdjustmentTypeSchema,
  InventoryBatchDtoSchema,
  InventoryBatchListingDtoSchema,
  InventoryBatchSchema,
  InventoryBatchStatusSchema,
  InventoryItemListingDtoSchema,
  InventoryLevelDtoSchema,
  InventoryListingQuerySchema,
  InventoryListingResponseSchema,
  InventoryPackageDtoSchema,
  InventoryPackageListingDtoSchema,
  InventoryPackageListingQuerySchema,
  InventoryPackageListingResponseSchema,
  InventoryPackageLocalStateSchema,
  InventoryPackageSchema,
  InventoryTransferDtoSchema,
  InventoryTransferListingDtoSchema,
  InventoryTransferStatusSchema,
  LocationAccessUserDtoSchema,
  MetrcAlreadyImportedRowSchema,
  MetrcCandidatePackageSchema,
  MetrcCrossLocationDuplicatesSummarySchema,
  MetrcCsvImportInputSchema,
  MetrcCsvImportResponseSchema,
  MetrcCsvRowSchema,
  MetrcDriftClassSchema,
  MetrcDriftDetectedRowSchema,
  MetrcFindCandidatesRequestSchema,
  MetrcFindCandidatesResponseSchema,
  MetrcImportContextSourceSchema,
  MetrcImportCreatedPackageSchema,
  MetrcImportMissingPackageSchema,
  MetrcImportPackagesRequestSchema,
  MetrcImportRecoveredPackageSchema,
  MetrcImportResultPackageSchema,
  MetrcImportResultRejectionSchema,
  MetrcImportResultResponseSchema,
  MetrcImportSkippedRowSchema,
  MetrcImportUpdateModeSchema,
  MetrcLabStatusSchema,
  MetrcMatchStrategySchema,
  MetrcPackageFacilitySchema,
  MetrcPackageFetchItemGroupSchema,
  MetrcPackageFetchResponseSchema,
  MetrcPackageFulfillmentLinkSchema,
  MetrcPackageListingDtoSchema,
  MetrcPackageListingQuerySchema,
  MetrcPackageListingResponseSchema,
  MetrcPackageProductionBatchSummarySchema,
  MetrcPackageRemoveRequestSchema,
  MetrcPackageRemoveResponseSchema,
  MetrcPackageReservationLinkSchema,
  MetrcVariantCaseConfigSchema,
  OrgBrandMetrcImportContextDtoSchema,
  ReceiveBatchInputSchema,
  StockLocationAccessResponseSchema,
  StockLocationActivityQuerySchema,
  StockLocationActivityResponseSchema,
  StockLocationAddressListItemSchema,
  StockLocationBrandAssignmentDtoSchema,
  StockLocationDetailDtoSchema,
  SupplierBrandListItemSchema,
  TransferListingQuerySchema,
  TransferListingResponseSchema,
  UpdateInventoryBatchInputSchema,
  UpdateInventoryLevelDtoSchema,
  UpdateInventoryPackageInputSchema,
  UpdateStockLocationBrandAssignmentsDtoSchema,
  UpdateStockLocationBrandsDtoSchema,
  UpdateTransferStatusInputSchema,
  UpsertStockLocationBrandAssignmentInputSchema
};
